import { useEffect, useState } from "react";

/**
 * Cinematic backdrop that morphs with scroll position:
 *   0.00 → predawn dark (#13171d)
 *   0.35 → dusk (deep blue + violet)
 *   0.70 → night with violet undertone
 *   0.85 → city silhouette + warm streetlight horizon
 *
 * Renders four absolutely-positioned layers; opacity for each is computed
 * from page scroll progress so they cross-fade.
 */

const clamp01 = (n) => Math.max(0, Math.min(1, n));

export default function ScrollAtmosphere() {
  const [p, setP] = useState(0);

  useEffect(() => {
    let rafPending = false;
    function onScroll() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        const max  = document.documentElement.scrollHeight - window.innerHeight;
        const next = max > 0 ? window.scrollY / max : 0;
        setP(clamp01(next));
      });
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Per-layer opacity
  const dusk    = clamp01((p - 0.10) * 2.2);
  const night   = clamp01((p - 0.45) * 2.3);
  const horizon = clamp01((p - 0.55) * 2.5);

  return (
    <div className="atmo" aria-hidden="true">
      {/* Layer 0 — predawn (always visible underneath) */}
      <div className="atmo-layer atmo-morning" />

      {/* Layer 1 — dusk: deep navy with hint of violet */}
      <div
        className="atmo-layer atmo-dusk"
        style={{ opacity: dusk }}
      />

      {/* Layer 2 — night: violet/aubergine */}
      <div
        className="atmo-layer atmo-night"
        style={{ opacity: night }}
      />

      {/* Layer 3 — warm horizon (city streetlight glow at bottom) */}
      <div
        className="atmo-horizon"
        style={{ opacity: horizon }}
      />

      {/* Layer 4 — city silhouette */}
      <CitySkyline opacity={horizon} />
    </div>
  );
}

function CitySkyline({ opacity }) {
  // Procedural silhouette: pattern of mismatched buildings
  return (
    <svg
      className="atmo-city"
      style={{ opacity }}
      viewBox="0 0 1440 220"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="bldg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#0b0810" />
          <stop offset="1" stopColor="#1a0c14" />
        </linearGradient>
      </defs>
      <path
        fill="url(#bldg)"
        d="M0,220 L0,140 L40,140 L40,110 L80,110 L80,150 L120,150 L120,90 L150,90 L150,60 L180,60 L180,95 L220,95 L220,130 L260,130 L260,80 L300,80 L300,40 L320,40 L320,75 L360,75 L360,120 L400,120 L400,95 L440,95 L440,135 L480,135 L480,70 L510,70 L510,30 L540,30 L540,85 L580,85 L580,125 L620,125 L620,100 L660,100 L660,160 L700,160 L700,115 L740,115 L740,55 L780,55 L780,100 L820,100 L820,140 L860,140 L860,90 L890,90 L890,50 L920,50 L920,110 L960,110 L960,150 L1000,150 L1000,80 L1040,80 L1040,40 L1070,40 L1070,95 L1110,95 L1110,130 L1150,130 L1150,75 L1190,75 L1190,115 L1230,115 L1230,155 L1270,155 L1270,90 L1310,90 L1310,60 L1340,60 L1340,105 L1380,105 L1380,140 L1440,140 L1440,220 Z"
      />
      {/* Window lights — small warm dots scattered on facades */}
      {WINDOWS.map((w, i) => (
        <rect
          key={i}
          x={w.x} y={w.y} width={w.s} height={w.s}
          fill={i % 4 === 0 ? "#FFB050" : "#FF8836"}
          opacity={0.55 + ((i % 5) * 0.08)}
          style={{ filter: "blur(.4px)" }}
        >
          <animate
            attributeName="opacity"
            values={`${0.45 + ((i % 5) * 0.06)};${0.95};${0.45 + ((i % 5) * 0.06)}`}
            dur={`${3 + (i % 7) * 0.5}s`}
            repeatCount="indefinite"
          />
        </rect>
      ))}
    </svg>
  );
}

// Deterministic window positions across the skyline path
const WINDOWS = (() => {
  const out = [];
  let seed = 1;
  const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  for (let i = 0; i < 90; i++) {
    out.push({
      x: 20 + rand() * 1400,
      y: 60 + rand() * 130,
      s: 2 + Math.floor(rand() * 2.4),
    });
  }
  return out;
})();
